import React, { FC, useEffect, useMemo, useRef, useState } from "react"

import {
  CalendarOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  LoadingOutlined, MenuOutlined,
  PrinterOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { Button, Collapse, Table, Tooltip, Popconfirm, Pagination, Space, Switch } from "antd"
import { ColumnsType } from "antd/es/table"
import BaseTable from "react-base-table"
import { useDispatch } from "react-redux"

import AddSupplier from "./components/AddSupplier"
import CancelOrderModal, { CancelOrderState } from "./components/CancelOrderModal"
import ChangeCisModal from "./components/ChangeCisModal"
import useStockFbsColumns from "./components/columns"
import FilterTable from "./components/Filter"
import SuppliersModal from "./components/SuppliersModal"
import { stockFBSMarketApi } from "../../common/api/endpoints/stockFBSMarketApi"
import { handleResponseError } from "../../common/api/middleware"
import { API_WS_URL } from "../../common/api/urls"
import Accordion from "../../common/components/accordion/Accordion"
import AdvancedDropdown from "../../common/components/dropdowns/AdvancedDropdown"
import ImportResultAlert from "../../common/components/import-result-alert/ImportResultAlert"
import MainLayout from "../../common/components/layouts/MainLayout"
import Loading from "../../common/components/loading/Loading"
import ColumnSettings from "../../common/components/modals/ColumnSettings"
import PageHead from "../../common/components/page-header/PageHeader"
import AutoWidthSelect from "../../common/components/selects/AutoWidthSelect"
import { defaultStockFBSFilters } from "../../common/constants/filters"
import { routeNames } from "../../common/constants/routes"
import { useActions } from "../../common/hooks/useActions"
import useTableWidth from "../../common/hooks/useTableWidth"
import { useTypedSelector } from "../../common/hooks/useTypedSelector"
import { projectIcons } from "../../common/icons/projectIcons"
import { TAppDispatch } from "../../redux/store"
import { MarketPlace, OrderStatus, PrintLabel, TransferToDelivery, UpdateOrderStatus } from "../../types/api"
import { getAccessToken } from "../../utils/auth"
import { marketPlacePictureMap } from "../../utils/data"
import { normalizeColumns, rowClassName } from "../../utils/function"
import { showErrorMessage, showSuccessByLength, showSuccessMessage } from "../../utils/ui"

/** Тип задания: перезагрузка заказов за неделю (только Wildberries) */
const WEEK_RELOAD_TASK_TYPE = 3

/** Ответ «Обновить статусы и стикеры» у WB; у OZON вместо него приходит строка-отчёт */
const UPDATE_ORDERS_PLAIN_SUCCESS = "Success"

/**
 * Показывает итог «Обновить статусы и стикеры»: у OZON бэк возвращает отчёт
 * («OZON: статусов обновлено N, этикеток скачано M, …») — его и показываем,
 * чтобы было видно, сколько реально обновилось. У WB — прежний общий текст.
 */
const showUpdateOrdersResult = (responseData: unknown): void => {
  if (typeof responseData === "string" && responseData.trim() && responseData !== UPDATE_ORDERS_PLAIN_SUCCESS) {
    showSuccessByLength(responseData.trim())
    return
  }

  showSuccessMessage("Статус успешно обновлен")
}

const Sbermarket: FC = () => {
  const [ isInitialized, setIsInitialized ] = useState(false)
  const [ filters, setFilters ] = useState<any>(defaultStockFBSFilters)
  const { filters: filtersFromResponse } = useTypedSelector((state) => state.stockFBS)

  const [ isVisible, setIsVisible ] = useState(true)
  const [ isSuppliesVisible,setIsSuppliesVisible ] = useState(false)
  const [ isSocket, setIsSocket ] = useState(false)

  const { isTwoCopiesPrint } =
      useTypedSelector((state) => state.currentUser)

  const { setPrintSettings } = useActions()

  const { isLoading,
    isLoadingLists,
    isUpdate,
    itemData,
    pagesData,
    companies,
    importResults,
    suppliersResults
  } =
      useTypedSelector((state) => state.stockFBS)
  const {
    stockFBSGetList,
    stockFBSGetImportResults,
    stockFBSGetSuppliersImportResults,
    setStockFBSUpdate,
    stockFBSGetCompanies,
    fetchFBSFilterList,
  } =
      useActions()
  const dispatch = useDispatch()
  const [ prevCisState, setPrevCisState ] = useState<{id: number | null, cis: string[] | null}>({ id: null, cis: null })
  const [ isManageColumnsVisible,setIsManageColumnsVisible ] = useState(false)
  const [ changeCisState, setChangeCisState ] =
    useState<{visible: boolean, id: number | null, cis: string[] | null}>({ visible: false, id: null, cis: null })
  const [ cancelOrderState, setCancelOrderState ] =
    useState<CancelOrderState>({ visible: false, orderIds: [], marketplaceId: null })
  const [ addSupplierState,setAddSupplierState ] = useState<{
    visible: boolean,
    id: null | number | undefined ,
    partner_id: null | number | undefined,
    name: string | undefined,
    marketplace_id:number | null | undefined
  }>({ visible:false,id:null,partner_id:null,name:"",marketplace_id:null })
  const [ checkSupplierState,setCheckSupplierState ] = useState<{
    visible: boolean,
    id: null | number | undefined ,
    msg: string
  }>({ visible:false,id:null,msg:"" })
  const [ partnersState,setPartnersState ] = useState<{
    values:number[]}>({
      values:[]
    })
  const handlePartnerIdsChange = (values:number[]) => {
    setPartnersState({ values })
  }

  /**
   * Перезагрузка заказов за неделю есть только у Wildberries: у OZON такого
   * режима нет, а обычный импорт и так берёт заказы за 30 дней. Кабинеты OZON
   * в этой кнопке не показываем и не отправляем — бэкенд их всё равно отсечёт.
   */
  const weekReloadCompanies = companies.filter(
    (company) => company.marketplace_id !== MarketPlace.OZON
  )
  const weekReloadSelectedIds = partnersState.values.filter((id) =>
    weekReloadCompanies.some((company) => company.id === id)
  )
  const [ isActionLoading,setIsActionLoading ] = useState(false)
  const [ isTransferToDeliveryLoading,setIsTransferToDeliveryLoading ] = useState(false)
  const [ transferSuppliers,setTransferSuppliers ] = useState<number | null>(null)

  const tableWidth = useTableWidth()
  const checkAndCloseSupplier = async (supplier_id:number) => {
    try{
      const response = await stockFBSMarketApi.checkAndCloseSupplier(supplier_id)
      if(response.data === "Success"){
        window.open(`${routeNames.stockSuppliers}/${supplier_id}`, "_blank")
      } else{
        setCheckSupplierState({
          visible:true,
          id:supplier_id,
          msg:response?.data
        })
      }
    }catch(err:any){
      handleResponseError(dispatch,err)
    }
  }
  const sadovodToBuild = async () => {
    try{
      setIsActionLoading(true)
      const { data } = await stockFBSMarketApi.savododToBuild()
      showSuccessMessage(data)
    }catch(err:any){
      handleResponseError(dispatch,err)
    }finally{
      setIsActionLoading(false)
    }
  }

  const handleMigrateOrders = async () => {
    try{
      setIsActionLoading(true)
      const { data } = await stockFBSMarketApi.migrateOldOrders()
      showSuccessMessage(data)
    }catch(err:any){
      handleResponseError(dispatch,err)
    }finally{
      setIsActionLoading(false)
    }
  }


  const handleCreateNewSupplier = (record:{id?:number | null,order_id?:string | null,marketplace_id?:number | null,partner_id?:number | null}) => {
    setAddSupplierState({
      visible:true,
      id:record?.id,
      partner_id:record?.partner_id,
      marketplace_id:record?.marketplace_id,
      name:""
    })
  }

  const isMobile = window.innerWidth <= 768
  const handleAction = async (action: number | string,params?:{order_ids:string[],marketplace_id:number,selectedRowKeys?:string[],cancel_reason_id?:number,cancel_reason_message?:string}) => {
    try{
      setIsActionLoading(true)
      const values = {
        status_id:action as OrderStatus,
        marketplace_id:params?.marketplace_id || filters?.marketplace_id,
        order_ids:params?.order_ids || selectedRowKeys,
        ...(isNotSelected && !params?.order_ids && { filters }),
        // Причина отмены есть только у OZON, для Wildberries поля не передаются
        ...(params?.cancel_reason_id && { cancel_reason_id:params.cancel_reason_id }),
        ...(params?.cancel_reason_message && { cancel_reason_message:params.cancel_reason_message })
      } as UpdateOrderStatus
      await stockFBSMarketApi.updateStatus(values)
      showSuccessMessage("Статус успешно обновлен")
    }catch (error:any) {
      const dispatch = (() => {}) as TAppDispatch
      handleResponseError(dispatch,error)
      console.log(error)
    }finally{
      setStockFBSUpdate(true)
      setIsActionLoading(false)
    }
  }

  const [
    columns,
    setColumns,
    defaultColumns,
    sorting,
    selectedRowKeys,
    setSelectedRowKeys
  ] = useStockFbsColumns({ setAddSupplierState,filters,handleAction, setChangeCisState, setCancelOrderState })

  const connectWebSocket = (attempt=1) => {
    const maxAttempts = 3
    const socket = new WebSocket(`${API_WS_URL}/?token=${getAccessToken()}`)

    socket.onmessage = (e) => {
      try {
        const updates = JSON.parse(e.data)
        if (updates) {
          if (updates?.["import-fbs-orders"]?.status === true) {
            stockFBSGetImportResults()
            stockFBSGetSuppliersImportResults()
            setIsSocket(true)
            setStockFBSUpdate(true)
          }
          if (updates?.["update-supplies"]?.status === true) {
            stockFBSGetSuppliersImportResults()
            // После «Сформировать отгрузку» заказы OZON переезжают из «К отгрузке» в «Сформированы»
            setStockFBSUpdate(true)
            setIsSocket(true)
          }
          if (updates?.["update-orders"]?.status === true) {
            setStockFBSUpdate(true)
            setIsSocket(true)
          }
        }
      } catch (e) {
        console.log(e)
      }
    }

    socket.onerror = () => {
      if (attempt < maxAttempts) {
        showErrorMessage(`Попытка ${attempt} для переподключения к сокетам...`)
        setTimeout(() => connectWebSocket(attempt + 1), 1500)
      } else {
        showErrorMessage("К сожалению подключение к сокетам не удалось, пожалуйста проверьте ваше интернет соеденение и перезагрузите страницу.")
      }
    }

    socket.onclose = () => {
      if (attempt < maxAttempts) {
        showErrorMessage(`Попытка ${attempt} для переподключения к сокетам...`)
        setTimeout(() => connectWebSocket(attempt + 1), 1500)
      } else {
        showErrorMessage("К сожалению подключение к сокетам не удалось, пожалуйста проверьте ваше интернет соеденение и перезагрузите страницу.")
      }
    }
  }

  useEffect(() => {
    stockFBSGetImportResults()
    stockFBSGetSuppliersImportResults()
    stockFBSGetCompanies()
    stockFBSGetList({
      page: 1,
      pageSize: 10,
      sorting: sorting,
      filters
    })
    connectWebSocket()
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isUpdate) {
      stockFBSGetList({
        page: pagesData.currentPage,
        pageSize: pagesData.pageSize,
        sorting: sorting,
        filters: filters
      },isSocket)
      setStockFBSUpdate(false)
    }
  }, [ isUpdate,isSocket ])

  useEffect(() => {
    if (isInitialized) {
      const isTimeSinceOrder = Object.keys(sorting)[0] === "time_since_order"
      const arrivalSorting = {
        arrival_date: sorting[Object.keys(sorting)[0]],
      }
      stockFBSGetList(
        {
          page: pagesData.currentPage,
          pageSize: pagesData.pageSize,
          sorting: isTimeSinceOrder ? arrivalSorting : sorting,
          filters: filters,
        },
        isSocket
      )
    }
  }, [ sorting ])

  useEffect(() => {
    fetchFBSFilterList()
  },[])

  const handleImportOrder = async (type:number, partnerIds:number[], event:React.MouseEvent<HTMLElement>) => {
    if((event.target as {className?:string})?.className !== "action"){
      return
    }
    if(partnerIds.length === 0){
      showErrorMessage("Выберите компании для которых требуется импорт.")
      return
    }
    try{
      const data = {
        partner_ids:partnerIds,
        type
      }
      await stockFBSMarketApi.addImportTask(data)
      showSuccessMessage("Задача на импорт успешно добавлена.")
    }catch(err){
      console.log(err)
      const responseErrorMessage = (err as unknown as {response:{data:{message:string}}}).response?.data?.message
      showErrorMessage(responseErrorMessage || "Произошла ошибка при отправке заказов на импорт")
    }
  }

  const handleImportExcel = async () => {
    try{
      await stockFBSMarketApi.downloadExcel({
        page: pagesData.currentPage,
        pageSize: pagesData.pageSize,
        sorting: sorting,
        filters: filters
      })
    }catch(err){
      console.log(err)
      const responseErrorMessage = (err as unknown as {response:{data:{message:string}}}).response?.data?.message
      showErrorMessage(responseErrorMessage || "Произошла ошибка при скачивании файла.")
    }
  }

  const isImportBtnDisabled = () => {
    return !importResults?.status
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
  }

  const isNotSelected = selectedRowKeys?.length === 0
  const isNotSelectedStatus = !filters?.status_id
  const isDisabledToBuild = ![ 1,3 ].includes(filters?.status_id) || isNotSelectedStatus
  const isDisabledToCollected = ![ 1,2 ].includes(filters?.status_id) || isNotSelectedStatus
  const isDisabledToNew = [ 1,5,6,7,8,9 ].includes(filters?.status_id) || (filters?.status_id === 3 && filters?.not_collected) || isNotSelectedStatus
  const isTransferToDeliveryDisabled = ![ 1,2,3 ].includes(filters?.status_id) || !filters?.partner_id || isNotSelectedStatus || !transferSuppliers
  /** Список отфильтрован по OZON: часть массовых операций маркетплейс не поддерживает */
  const isOzonFilter = filters?.marketplace_id === MarketPlace.OZON

  const handleIconAction = async (action: "printLabel" | "printSticker" | "updateOrders") => {
    try{
      setIsActionLoading(true)
      const values = {
        ...(!isNotSelected && { order_ids:selectedRowKeys }),
        ...(isNotSelected && { filters }),
        ...(isNotSelected && { sorting })
      } as PrintLabel
      if (action === "updateOrders") {
        const response = await stockFBSMarketApi.updateOrders(values)
        showUpdateOrdersResult(response?.data)
      } else {
        await stockFBSMarketApi[action](values,false)
      }
    }catch (error:any) {
      const dispatch = (() => {}) as TAppDispatch
      handleResponseError(dispatch,error)
      console.log(error)
    }finally{
      setStockFBSUpdate(true)
      setIsActionLoading(false)
    }
  }

  const handleTransferToDelivery = async () => {
    try{
      setIsTransferToDeliveryLoading(true)
      const values = {
        supply_id :transferSuppliers,
        marketplace_id:filters?.marketplace_id,
        partner_id :filters?.partner_id,
        ids:selectedRowKeys.map((el)=>itemData.find((item)=>item?.order_id.toString() === el.toString())?.order_id)
      } as TransferToDelivery
      await stockFBSMarketApi.transferToDelivery(values)
      showSuccessMessage("Заказы успешно переведены в поставку.")
      setSelectedRowKeys([])
    }catch (error:any) {
      const dispatch = (() => {}) as TAppDispatch
      handleResponseError(dispatch,error)
      console.log(error)
    }finally{
      setStockFBSUpdate(true)
      setIsTransferToDeliveryLoading(false)
    }
  }
  useEffect(() => {
    setSelectedRowKeys([])
  }, [ filters ])

  useEffect(() => {
    setTransferSuppliers(null)
  }, [ filtersFromResponse?.supplies_for_transfer ])

  const dynamicStyles = useMemo(() => {
    const styles: any = {}
    itemData.forEach((item: any) => {
      if (item.line_color) {
        const className = `row-color-${item.line_color?.replace("#", "")}`
        styles[className] = { backgroundColor: `#${item.line_color}` }
      }
    })
    return styles
  }, [ itemData ])

  const StockSuppliersColumns: ColumnsType<any> = [
    {
      title: "Маркетплейс",
      dataIndex: "marketplace_id",
      key: "marketplace_id",
      align:"center",
      sorter: (a, b) => a.marketplace_id - b.marketplace_id,
      ellipsis: {
        showTitle: false,
      },
      width: 80,
      render: (val) => (
        <img alt={"Маркетплейс"} style={{ borderRadius: "50%", width: "28px" }}
          src={marketPlacePictureMap[val]}/>
      ),
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: {
        showTitle: false,
      },
      width: 80,
      render: (val) => (
        <Tooltip placement="topLeft" title={val}>
          {val}
        </Tooltip>
      ),
    },
    {
      title: "Внешний номер",
      dataIndex: "external_id",
      key: "external_id",
      sorter: (a, b) => a.external_id.localeCompare(b.external_id),
      ellipsis: {
        showTitle: false,
      },
      width: 90,
      render: (val) => (
        <Tooltip placement="topLeft" title={val}>
          {val && <strong style={{ background:"#f3f6fa",padding:"8px" }}>
            {val}
          </strong> }
        </Tooltip>
      ),
    },
    {
      title: "Организация",
      dataIndex: "org",
      key: "org",
      sorter: (a, b) => a.org.localeCompare(b.org),
      ellipsis: {
        showTitle: false
      },
      width: 150,
      render: (val) => (
        <Tooltip placement="topLeft" title={val}>
          {val}
        </Tooltip>
      )
    },
    {
      title: "Дата",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
      ellipsis: {
        showTitle: false,
      },
      width: 120,
      render: (val) => (
        <Tooltip placement="topLeft" title={val}>
          <CalendarOutlined style={{ marginRight: "5px" }} />
          {val}
        </Tooltip>
      ),
    },
    {
      title: "Действия",
      key: "operation",
      width: 100,
      render: (val, record) => {
        // У OZON отгрузку формирует маркетплейс: проверка несобранных заказов не нужна, дальше — окно с количеством грузовых мест
        const isOzonSupply = record.marketplace_id === MarketPlace.OZON
        return <Tooltip placement="topLeft" title={isOzonSupply ? "Сформировать отгрузку" : "Проверить и закрыть"}>
          <div style={{ display:"flex",justifyContent:"center",alignItems:"center" }}>
            <span onClick={()=>checkAndCloseSupplier(record.id)} style={{ color:"rgb(64, 150, 255)",cursor:"pointer" }}>
              {isOzonSupply ? "Сформировать" : "Сохранить закрыть"}
            </span>
          </div>
        </Tooltip>
      },
    }
  ]

  const handleResetColumns = () => {
    setColumns(defaultColumns)
    localStorage.setItem("savedDropSupplierCols",JSON.stringify(normalizeColumns(defaultColumns)))
    showSuccessMessage("Порядок успешно возвращен.")
  }

  const handlePagination = (page:number,pageSize:number) => {
    stockFBSGetList({
      page,
      pageSize,
      sorting,
      filters
    },isSocket)
  }

  function handlePrintSettingChange() {
    setPrintSettings(isTwoCopiesPrint === 1 ? 2 : 1)
  }

  return (
    <>
      <style>
        {Object.keys(dynamicStyles).map(className => {
          const style = dynamicStyles[className]
          return `
            .${className} {
              background-color: ${style.backgroundColor};
            }
          `
        })}
      </style>
      <MainLayout pageTitle="Заказы на отгрузку">

        <div className="page-wrapper" style={{ ...(isMobile ? { width:"100vw" } : {}) }}>
          <PageHead
            titleStyles={{ fontWeight: "600" }}
            title="Заказы на отгрузку"
            extra={
              <div>
              </div>
            }
          />
          <div style={{ display: "flex", columnGap: "5px", marginBottom: "16px", marginLeft: "24px" }}>
            <Button
              type="primary"
              className={"blueButton"}
              onClick={(event) => handleImportOrder(1, partnersState.values, event)}
              disabled={isImportBtnDisabled()}
              loading={isLoading}
              style={{ paddingRight: 0,
                background:"#00a2e8"
              }}
            >
              <span className={"action"}>
                {projectIcons.sberImportIcon({ style: { marginRight: "5px" } })}
                ИМПОРТ ЗАКАЗОВ
              </span>

              <span style={{
                position: "absolute",
                marginTop: "-5px",
                marginLeft: "5px",
                marginRight: "5px",
                height: "100%"
              }}>
              </span>
              <span style={{ width: "6px" }}></span>
              <AdvancedDropdown
                items={companies}
                selectedIds={partnersState.values}
                setSelectedIds={(values) => {
                  handlePartnerIdsChange(values)
                }}
              />
            </Button>
            <Button
              type="primary"
              onClick={(event) => handleImportOrder(2, partnersState.values, event)}
              disabled={isImportBtnDisabled()}
              loading={isLoading}
              style={{ paddingRight: 0 , background:"#00a2e8" }}
              className={"blueButton"}
            >
              <span className={"action"}>
                {projectIcons.sberImportIcon({ style: { marginRight: "5px" } })}
                ИМПОРТ С ОБНОВЛЕНИЕМ ОСТАТКОВ
              </span>
              <span style={{
                position: "absolute",
                marginTop: "-5px",
                marginLeft: "5px",
                marginRight: "5px",
                height: "100%"
              }}>
              </span>
              <span style={{ width: "6px" }}></span>
              <AdvancedDropdown
                items={companies}
                selectedIds={partnersState.values}
                setSelectedIds={(values) => {
                  handlePartnerIdsChange(values)
                }}
              />
            </Button>
            <Button
              type="primary"
              onClick={(event) => handleImportOrder(WEEK_RELOAD_TASK_TYPE, weekReloadSelectedIds, event)}
              disabled={isImportBtnDisabled()}
              loading={isLoading}
              style={{ paddingRight: 0 , background:"#00a2e8" }}
              className={"blueButton"}
            >
              <span className={"action"}>
                {projectIcons.sberImportIcon({ style: { marginRight: "5px" } })}
                ПЕРЕЗАГРУЗИТЬ ЗАКАЗЫ ЗА НЕДЕЛЮ
              </span>
              <span style={{
                position: "absolute",
                marginTop: "-5px",
                marginLeft: "5px",
                marginRight: "5px",
                height: "100%"
              }}>
              </span>
              <span style={{ width: "6px" }}></span>
              <AdvancedDropdown
                items={weekReloadCompanies}
                selectedIds={weekReloadSelectedIds}
                setSelectedIds={(values) => {
                  handlePartnerIdsChange(values)
                }}
              />
            </Button>
          </div>
          <Accordion title={"Результаты импорта"} isVisible={isVisible} toggleVisibility={toggleVisibility}>
            {importResults?.items?.map((result) => (
              <ImportResultAlert
                key={result.marketplace_id}
                marketplace_id={result.marketplace_id}
                message={result.msg}
                color={result.color}
              />
            ))}
          </Accordion>
          <Accordion title={"Поставки"} isVisible={isSuppliesVisible} toggleVisibility={()=>setIsSuppliesVisible(!isSuppliesVisible)}>
            <Table
              virtual={true}
              columns={StockSuppliersColumns}
              dataSource={suppliersResults}
              loading={{
                spinning: isLoading,
                indicator: <LoadingOutlined style={{ fontSize: 24 }} spin/>
              }}
              rowKey={"id"}
              size="small"
              bordered
            />
          </Accordion>
          <div className="page-content" style={{ paddingTop: 0 }}>
            <Collapse>
              <Collapse.Panel header="Фильтр заказов" key="1">
                <FilterTable
                  isLoading={isLoadingLists || isLoading}
                  setFilters={setFilters}
                  sorting={sorting}
                />
              </Collapse.Panel>
            </Collapse>

            {isOzonFilter && (
              <div style={{ color: "#888", marginBottom: "8px" }}>
                Этикетки OZON появляются через 1–2 минуты после перевода в СОБРАН —
                нажмите «Обновить статусы и стикеры» 🔄, чтобы их скачать.
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              {/* У OZON нет промежуточных статусов: сотрудник либо собирает, либо отменяет заказ */}
              {!isOzonFilter && (
                <>
                <Popconfirm
                  title={<span>Подтверждаете изменение статуса?<br/> Статус сменится у всех заказов <br/> согласно выбранным параметрам фильтра.</span>}
                  onConfirm={() => handleAction(1)}
                  placement={"bottom"}
                  disabled={selectedRowKeys?.length !== 0}
                >
                  <Tooltip
                    title="Переводит все отмеченные заказы в НОВЫЕ. В Новые можно вернуть, только заказы со статусами НА СБОРКЕ или СОБРАН">
                    <Button
                      disabled={isDisabledToNew}
                      onClick={selectedRowKeys?.length !== 0 ? () => handleAction(1) : undefined}
                      style={{
                        ...buttonStyle,
                        borderRadius: "4px 0 0 4px", ...(isDisabledToNew && { backgroundColor: "gray" })
                      }}
                    >
                      НОВЫЙ
                    </Button>
                  </Tooltip>
                </Popconfirm>

                <Popconfirm
                  title={<span>Подтверждаете изменение статуса?<br/> Статус сменится у всех заказов <br/> согласно выбранным параметрам фильтра.</span>}
                  onConfirm={() => handleAction(2)}
                  placement={"bottom"}
                  disabled={selectedRowKeys?.length !== 0}
                >
                  <Tooltip
                    title="Переводит все отмеченные заказы НА СБОРКУ. На сборку можно перевести, только заказы со статусами НОВЫЙ или СОБРАН">
                    <Button
                      disabled={isDisabledToBuild}
                      style={{
                        ...buttonStyle,
                        borderRadius: 0, ...(isDisabledToBuild && { backgroundColor: "gray" })
                      }}
                      onClick={selectedRowKeys?.length !== 0 ? () => handleAction(2) : undefined}
                    >
                      НА СБОРКЕ
                    </Button>
                  </Tooltip>
                </Popconfirm>
                </>
              )}
              <Popconfirm
                title={<span>Подтверждаете изменение статуса?<br/> Статус сменится у всех заказов <br/> согласно выбранным параметрам фильтра.</span>}
                onConfirm={() => handleAction(3)}
                placement={"bottom"}
                disabled={selectedRowKeys?.length !== 0}
              >
                <Tooltip
                  title="Переводит все отмеченные заказы в СОБРАН. В собран можно перевести, только заказы со статусами НОВЫЙ или НА СБОРКЕ">
                  <Button
                    disabled={isDisabledToCollected}
                    onClick={selectedRowKeys?.length !== 0 ? () => handleAction(3) : undefined}
                    style={{
                      ...buttonStyle,
                      // при фильтре OZON «СОБРАН» — первая кнопка в группе, скругляем её левый край
                      borderRadius: isOzonFilter ? "4px 0 0 4px" : 0,
                      ...(isDisabledToCollected && { backgroundColor: "gray" })
                    }}
                  >
                    СОБРАН
                  </Button>
                </Tooltip>
              </Popconfirm>
              <Tooltip title="Печать стикеров для всех выбранных заказов">
                <Button
                  disabled={isActionLoading}
                  style={{
                    ...iconButtonStyle,
                    borderRadius: 0,
                    backgroundColor: isActionLoading ? "gray" : "#00a2e8",
                  }}
                  onClick={() => handleIconAction("printSticker")}
                  icon={<PrinterOutlined/>}
                />
              </Tooltip>
              <Tooltip title={isOzonFilter
                ? "Печать этикеток товара для всех выбранных заказов: по этикетке на каждую единицу товара в отправлении"
                : "Печать этикеток для всех выбранных заказов"}>
                <Button
                  disabled={isActionLoading}
                  onClick={() => handleIconAction("printLabel")}
                  style={{
                    ...iconButtonStyle,
                    borderRadius: 0,
                    backgroundColor: isActionLoading ? "gray" : "#00a2e8",
                  }}
                  icon={<FilePdfOutlined/>}
                />
              </Tooltip>
              <Tooltip title={isOzonFilter
                ? "Обновить статусы заказов OZON, скачать этикетки собранных и обновить отгрузки"
                : "Обновить статусы и стикеры заказов, там где их нет"}>
                <Button
                  disabled={isActionLoading}
                  style={{
                    ...iconButtonStyle,
                    borderRadius: "0 4px 4px 0",
                    borderLeft: "none",
                    backgroundColor: isActionLoading ? "gray" : "#00a2e8"
                  }}
                  onClick={() => handleIconAction("updateOrders")}
                  icon={<ReloadOutlined/>}
                />
              </Tooltip>

              <div style={{ display:"flex", marginLeft: "auto", alignItems:"center",columnGap:"8px" }}>
                <Switch
                  checked={isTwoCopiesPrint === 1}
                  onChange={handlePrintSettingChange}
                  className={"orange-switcher"} />
                <span style={{ fontWeight: 600, fontSize: 16 }}>
                  ПЕЧАТАТЬ/СКАЧИВАТЬ ПО 2 КОПИИ
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {isOzonFilter ? (
                <div style={{ maxWidth: "560px" }}>
                  <span style={{ fontWeight: 600, fontSize: 16 }}>ОТГРУЗКА OZON</span>
                  <div style={{ marginTop: "10px", color: "#555" }}>
                    Отгрузку OZON создаёт маркетплейс сам при переводе заказов в СОБРАН:
                    она забирает все собранные заказы склада. Найти её можно в разделе «Поставки».
                  </div>
                </div>
              ) : (
                <div>
                  <span style={{ fontWeight: 600, fontSize: 16 }}>ПЕРЕНЕСТИ В ПОСТАВКУ  <Tooltip
                    placement="bottom"
                    title={
                      <div
                        className="page-title-tooltip-text"
                        dangerouslySetInnerHTML={{ __html: sendToSuppliesText }}
                      ></div>
                    }
                    color="#fff"
                    zIndex={9999}
                    trigger="click"
                    overlayClassName="page-title-tooltip"
                  >
                    <QuestionCircleOutlined style={{ color: "#ccc", fontSize: 14 }}/>
                  </Tooltip></span>
                  <div style={{ display: "flex", columnGap: "16px", alignItems: "center", marginTop: "10px" }}>
                    <AutoWidthSelect
                      value={transferSuppliers as unknown as undefined}
                      options={[ ...(filtersFromResponse?.supplies_for_transfer || []), {
                        id: -1,
                        name: "Создать новую",
                      } ]}
                      placeholder={"Выберите поставку"}
                      style={{
                        minWidth: "180px",
                      }}
                      onChange={(value) => {
                        if (value === -1) {
                          const values = {
                            id: null,
                            partner_id: filters?.partner_id,
                            marketplace_id: filters?.marketplace_id
                          }
                          if (!filters?.partner_id) {
                            showErrorMessage("Выберите партнера, чтобы продолжить.")
                            setTransferSuppliers(null)
                            return
                          }
                          handleCreateNewSupplier(values)
                          setTransferSuppliers(null)
                          return
                        } else {
                          setTransferSuppliers(value)
                        }
                      }}
                    />
                    <Tooltip
                      placement="bottom"
                      title={
                        <div
                          className="page-title-tooltip-text"
                          dangerouslySetInnerHTML={{ __html: "Выберете обязательные фильтры: организация, маркетплейс, статус НОВЫЙ, НА СБОРКЕ или СОБРАН." }}
                        ></div>
                      }
                      color="#fff"
                      zIndex={9999}
                      trigger="hover"
                      overlayClassName="page-title-tooltip"
                    >
                      <Button
                        style={{
                          ...iconButtonStyle,
                          width: "unset",
                          height: "unset",
                          ...((isTransferToDeliveryDisabled || isTransferToDeliveryLoading) && { backgroundColor: "gray" })
                        }}
                        type={"primary"}
                        disabled={isTransferToDeliveryDisabled || isTransferToDeliveryLoading}
                        onClick={handleTransferToDelivery}>Перенести</Button>
                    </Tooltip>
                  </div>
                </div>
              )}
              <Space>
                {/* Перенос старых заказов и «Садовод» — операции только для Wildberries */}
                {!isOzonFilter && (
                  <>
                  <Button
                    type="primary"
                    disabled={isActionLoading}
                    loading={isActionLoading}
                    onClick={handleMigrateOrders}
                    className={"blueButton"}
                    style={{
                      height: "40px",
                      fontSize:15,
                      ...((isActionLoading) && { backgroundColor: "gray" })
                    }}
                  >
                    Перенести старые заказы
                  </Button>
                  <Button
                    type="primary"
                    disabled={isActionLoading}
                    loading={isActionLoading}
                    onClick={sadovodToBuild}
                    className={"blueButton"}
                    style={{
                      height: "40px",
                      fontSize:15,
                      ...((isActionLoading) && { backgroundColor: "gray" })
                    }}
                  >
                    Садовод на сборку
                  </Button>
                  </>
                )}
                <Tooltip title="Выберите заказы для экспорта.">
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined/>}
                    onClick={handleImportExcel}
                    style={{
                      backgroundColor: "#28a745",
                      borderColor: "#28a745",
                      borderRadius: "4px",
                      color: "#fff",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    СКАЧАТЬ В EXCEL
                  </Button>
                </Tooltip>
              </Space>
            </div>
            <div className="universal-table-settings" style={{ width: tableWidth }}>
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "10px" }}>
                <Button
                  style={{ border: "none", fontWeight: "400", color: "#4096ff" }}
                  onClick={() => setIsManageColumnsVisible(true)}
                >
                  <MenuOutlined
                    style={{ transform: "rotate(90deg)", fontSize: "16px", color: "#4096ff" }}
                  />
                  СТОЛБЦЫ
                </Button>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    style={{ border: "none", fontWeight: "400", color: "#4096ff" }}
                    onClick={handleResetColumns}
                  >
                    ВЕРНУТЬ ПОРЯДОК СТОЛБЦОВ
                  </Button>
                </div>
              </div>
              <Loading loading={isLoading}>
                <BaseTable
                  columns={columns as any}
                  data={itemData}
                  loading={isLoading}
                  fixed
                  width={tableWidth}
                  height={600}
                  disabled={isLoading}
                  rowKey="order_id"
                  rowHeight={150}
                  rowClassName={rowClassName}
                  onEndReachedThreshold={300}
                />
              </Loading>
            </div>
            <Pagination
              disabled={isLoading}
              style={{ width: isMobile ? "100vw" : "100%", marginBottom: "40px" }}
              onChange={handlePagination}
              size={"small"}
              current={pagesData.currentPage}
              showSizeChanger={true}
              pageSizeOptions={[ "10","50", "100", "200", "500","1000" ]}
              defaultCurrent={1}
              defaultPageSize={10}
              total={pagesData.total}
              showTotal={(total) => `Всего строк: ${total}`}
            />
          </div>
          {
            isManageColumnsVisible &&
              <ColumnSettings setVisible={setIsManageColumnsVisible}
                defaultColumns={defaultColumns}
                columns={columns}
                localStorageKey={"savedFBSColumns"}
                setColumns={setColumns}/>
          }
          {
            addSupplierState.visible && <AddSupplier addState={addSupplierState} setAddState={setAddSupplierState}/>
          }
          {
            checkSupplierState.visible &&
              <SuppliersModal state={checkSupplierState} setState={setCheckSupplierState}/>
          }
          <CancelOrderModal
            state={cancelOrderState}
            setState={setCancelOrderState}
            onConfirm={(orderIds, marketplaceId, reasonId, message) =>
              handleAction(OrderStatus.Cancel, {
                order_ids: orderIds,
                marketplace_id: marketplaceId,
                cancel_reason_id: reasonId,
                cancel_reason_message: message
              })
            }
          />
          {
            changeCisState.visible && <ChangeCisModal 
              setPrevState={setPrevCisState}
              prevState={prevCisState}
              state={changeCisState}
              setState={setChangeCisState}/>
          }
        </div>
      </MainLayout>
    </>
  )
}

const buttonStyle = {
  backgroundColor: "#00a2e8",
  color: "#fff",
  borderRadius: "4px",
  border: "none",
  fontSize: "14px",
  fontWeight: "400",
  height: "40px",
  padding: "0 16px",
}

const iconButtonStyle = {
  backgroundColor: "#00a2e8",
  color: "#fff",
  borderRadius: "4px",
  border: "none",
  fontSize: "14px",
  fontWeight: "bold",
  height: "40px",
  width: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const sendToSuppliesText = "Выберете заказы, которые хотите массово добавить в поставку на маркетплейсе. Сменить поставку можно только для заказов со статусами НОВЫЙ, НА СБОРКЕ или СОБРАН."


export default Sbermarket
